<template>
  <div class="card">
    <b-skeleton-wrapper :loading="isLoading">
      <template #loading>
        <div class="card-body">
          <b-skeleton width="85%"></b-skeleton>
          <b-skeleton width="55%"></b-skeleton>
          <b-skeleton width="70%"></b-skeleton>
        </div>
      </template>
      <div class="card-body">
        <div class="row mb-4">
          <div class="col-md-3">
            <div class="d-flex flex-wrap">
              <div>
                <h5 class="mb-1">{{ $t('dashboard.title.dailyFuelComparation') }}</h5>
                <p class="mb-0 font-size-10">
                  {{ momentDateShortMonth(period?.[0]) }} -
                  {{ momentDateShortMonth(period?.[1]) }}
                </p>
              </div>
            </div>
          </div>
          <div class="col-md-3">
            <div class="d-flex align-items-center mc-table-view">
              <span class="mc-label">{{ $t('global.object.period') }}</span>
              <mc-date-picker
                class="ms-2"
                v-model="period"
                :placeholder="$t('global.action.selectDate')"
                format="DD MMM YYYY"
                value-type="YYYY-MM-DD"
                :shortcuts="makeShortcuts"
                :draft="true"
                :disabled="isLayoutEdit"
                range-separator=" - "
                range
                :confirm="true"
                :confirm-text="$t('global.action.apply')"
                ref="periodePicker"
                :disabled-date="disabledRange"
                @pick="pickDate"
                :clearable="false"
                @input="applyFilter"
                :lang="datePickerLang"
              ></mc-date-picker>
            </div>
          </div>
          <div class="col-md-4">
            <div class="d-flex align-items-center mc-table-view">
              <span class="mc-label">{{ $t('global.field.vehicleType') }}</span>
              <multiselect
                class="ms-2 w-50"
                v-model="fleetCategory"
                :options="categoryOptions"
                track-by="vehicleTypeId"
                label="label"
                :placeholder="$t('global.field.vehicleType')"
                :multiple="true"
                :taggable="true"
                @input="applyFilter"
              >
                <template slot="selection" slot-scope="{ values, isOpen }">
                  <div class="multiselect__tags-wrap" v-if="values.length && !isOpen">
                    <span class="multiselect__tag">
                      <span>{{ values[0].label }}</span>
                      <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon"></i>
                    </span>
                    <span
                      class="badge bg-success px-2 py-1 d-inline-block"
                      v-if="values.length > 1 && !isOpen"
                      >+{{ values.length - 1 }}</span
                    >
                  </div>
                </template>
              </multiselect>
            </div>
          </div>
          <div class="col-md-2">
            <b-button-group class="mb-3">
              <b-button @click="changeTab('fuel')" :variant="isFuel ? 'primary' : 'outline-primary'"
                ><i class="mc-fa icon-outline_droplet"></i>
                {{ $t('dashboard.text.litre') }}</b-button
              >
              <b-button @click="changeTab('cost')" :variant="isFuel ? 'outline-primary' : 'primary'"
                ><i class="mc-fa icon-outline_rupiah"></i>
                {{ $t('dashboard.text.rupiah') }}</b-button
              >
            </b-button-group>
          </div>
        </div>
        <div v-if="isFuel">
          <BarChart
            :labels="fuelChartData.labels"
            :datasets="fuelChartData.datasets"
            :yLabel="$t('dashboard.text.litre')"
            ref="barChartRef"
          >
          </BarChart>
        </div>
        <div v-if="!isFuel">
          <BarChart
            :labels="costChartData.labels"
            :datasets="costChartData.datasets"
            :yLabel="$t('dashboard.text.rupiah')"
          >
          </BarChart>
        </div>
      </div>
    </b-skeleton-wrapper>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import BarChart from '@/components/Chart/Bar.vue'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import { GET_SUMMARY_FUEL_COMPARISON } from '@/services/api/dashboard/dashboard'
import datepickerShortcuts from '../lib/constants/date-picker-shortcuts'
import datePickerLang from '@/helpers/date-picker-lang'
import McDatePicker from '@/components/McDatePicker'
import { mapGetters } from 'vuex'
moment.locale('id')

export default {
  components: { Multiselect, BarChart, McDatePicker },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_TYPE,
      variables() {
        return {
          filter: {
            hasFuelSensor: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.categoryOptions = data.vehicleTypes.vehicleTypes.data.map((item) => {
            return {
              vehicleTypeId: item.vehicleTypeId,
              label: `${item.model}/${item.type}`,
            }
          })
          this.fleetCategory = this.categoryOptions
        }
      },
    },
  },
  props: {
    isLayoutEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      phaseOneShow: false,
      isLoading: false,
      isFuel: false,
      period: [moment().subtract(6, 'd').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      pastDate: moment().subtract(1, 'years'),
      futureEnd: moment().add(1, 'years'),
      fleetCategory: [],
      categoryOptions: [],
      fuelChartData: {
        labels: [],
        datasets: [],
      },
      costChartData: {
        labels: [],
        datasets: [],
      },
      vgroupId: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    makeShortcuts() {
      return datepickerShortcuts(this, 'daily-comparison-config', {
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      })
    },

    datePickerLang() {
      return datePickerLang()
    },
  },
  async mounted() {
    this.isLoading = true
    let config = JSON.parse(localStorage.getItem('daily-comparison-config'))
    if (!config) {
      await this.initLocalStorage()
      config = JSON.parse(localStorage.getItem('daily-comparison-config'))
    }
    await this.$nextTick()
    this.loadLocalStorage(config)
  },
  methods: {
    disabledRange: function (date) {
      return date <= this.pastDate || date >= this.futureEnd || date > new Date()
    },
    pickDate(date) {
      let maxDate = 7
      this.pastDate = new Date(date.getTime() - 24 * maxDate * 3600 * 1000)

      // Get the current date and time
      let currentDate = new Date()

      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000).getTime() === currentDate.getTime()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    async initLocalStorage() {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId: this.vgroupId ?? undefined,
        },
      })
      localStorage.setItem(
        'daily-comparison-config',
        JSON.stringify({
          startDate: this.period[0],
          endDate: this.period[1],
          vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
          isShortcut: false,
          index: 0,
        })
      )
    },
    async loadLocalStorage(data) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId: this.vgroupId ?? undefined,
        },
      })
      if (data.isShortcut) {
        const generateDate = this.makeShortcuts[data.index].onClick()
        this.period[0] = this.momentYmD(generateDate[0])
        this.period[1] = this.momentYmD(generateDate[1])
      } else {
        this.period[0] = data.startDate
        this.period[1] = data.endDate
      }

      this.fleetCategory = this.categoryOptions.filter((item) =>
        data.vehicleTypeId.includes(item.vehicleTypeId)
      )
      this.generateChart(this.vgroupId)
    },
    changeTab(params) {
      this.isLoading = true
      if (params == 'fuel') {
        this.isFuel = true
      } else {
        this.isFuel = false
      }
      setTimeout(() => {
        this.isLoading = false
      }, 1000)
    },
    applyFilter() {
      const config = JSON.parse(localStorage.getItem('daily-comparison-config'))
      const filter = {
        startDate: this.period[0],
        endDate: this.period[1],
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
        isShortcut: false,
        index: 0,
      }
      if (!config.isShortcut) {
        localStorage.setItem('daily-comparison-config', JSON.stringify(filter))
      }
      this.generateChart(this.vgroupId)
    },
    async loadVehicleTypes(vgroupId) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId: this.vgroupId ?? undefined,
        },
      })
    },
    async generateChart(vgroupId) {
      this.isLoading = true
      this.vgroupId = vgroupId
      const filters = {
        startDate: this.period[0],
        endDate: this.period[1],
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      }
      const res = await this.$apollo.query({
        query: GET_SUMMARY_FUEL_COMPARISON,
        variables: {
          filter: {
            ...filters,
            column: [
              'sum_actual_efficiency',
              'sum_actual_total_fuel',
              'sum_standard_fuel',
              'sum_actual_price',
              'sum_standard_price',
              'sum_total_distance',
              'sum_total_hours',
            ],
            orderBy: JSON.stringify({ date: 'asc' }),
            vgroupId: this.vgroupId ?? undefined,
            disabledOdoFuel: this.hasAccess(['b1x-new-fuel-expectation:read']),
          },
        },
        fetchPolicy: 'no-cache',
      })

      const data = res.data.summaryFuelDayComparison.summaryFuelDayComparison.data
      //fuel chart
      this.fuelChartData.labels = data.map((item) => moment(item.date).format('DD MMM'))
      this.fuelChartData.datasets = [
        {
          type: 'line',
          label: this.$t('global.standard'),
          data: data.map((item) => this.minimizeFloat(item.sumStandardFuel)),
          borderColor: '#f1734f',
          fill: false,
          pointRadius: 2,
          lineTension: 0,
          borderWidth: 4,
        },
        {
          type: 'bar',
          label: this.$t('global.actual'),
          data: data.map((item) => this.minimizeFloat(item.sumActualTotalFuel)),
          borderColor: '#0d4491',
          backgroundColor: '#0d4491',
          barPercentage: 0.4,
        },
      ]

      //cost chart
      this.costChartData.labels = data.map((item) => moment(item.date).format('DD MMM'))
      this.costChartData.datasets = [
        {
          type: 'line',
          label: this.$t('global.standard'),
          data: data.map((item) => this.minimizeFloat(item.sumStandardPrice)),
          borderColor: '#f1734f',
          fill: false,
          pointRadius: 2,
          lineTension: 0,
          borderWidth: 4,
        },
        {
          type: 'bar',
          label: this.$t('global.actual'),
          data: data.map((item) => this.minimizeFloat(item.sumActualPrice)),
          borderColor: '#0d4491',
          backgroundColor: '#0d4491',
          barPercentage: 0.4,
        },
      ]

      setTimeout(() => {
        this.isLoading = false
      }, 1000)
    },
  },
}
</script>
